import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ShadowDomOverlayContainer
    extends OverlayContainer
    implements OnDestroy
{
    override _document = inject(DOCUMENT);
    private _observer: MutationObserver | null = null;
    private _shadowRoot: ShadowRoot | null = null;
    private _schemeObserver: MutationObserver | null = null;
    private _innerRoot: HTMLElement | null = null;
    private _watchedAppShadowRoot: ShadowRoot | null = null;

    // When set by the host via clientTheme input, this class takes precedence over the body's theme class.
    private _clientThemeClass: string | null = null;

    /**
     * Called by LayoutWebComponent when a clientTheme input is provided so that
     * overlays (dialogs, menus, selects, etc.) use the correct theme class instead
     * of inheriting the host body's theme.
     */
    public setClientThemeClass(themeClass: string): void {
        if(themeClass){
            this._clientThemeClass = themeClass;
        }
        this._syncSchemeFromBody();
    }

    // Tracks CSS content already present in the overlay shadow root.
    // Keyed by a cheap fingerprint (length + head + tail) rather than the full
    // string so the Set stays small regardless of stylesheet size.
    private readonly _mirroredStyles = new Set<string>();

    // ── Overlay patterns ────────────────────────────────────────────────────
    // Only styles whose content matches one of these patterns are pre-copied
    // from dms-app.shadowRoot into the overlay shadow root.  App-specific
    // component styles (file-manager list, sidebar, …) are excluded; if they
    // ever render inside an overlay the MutationObserver forwards them then.
    private static readonly _OVERLAY_PATTERNS = [
        '.mat-',              // Angular Material components
        '.mdc-',              // MDC primitives (Angular Material v17)
        '.cdk-',              // Angular CDK overlays, drag, virtual-scroll
        'fuse-confirmation',
        '.ng-dropdown-panel', // ng-select
        '.ng-select',
        '.ng-option',
        '.ng2-tag-input',     // ngx-chips
        '.tag-input',
    ];

    // ── Public API ──────────────────────────────────────────────────────────

    public override getContainerElement(): HTMLElement {
        if (!this._containerElement || !this._containerElement.isConnected) {
            // Full reset: the overlay host was removed from the document.
            this._containerElement?.remove();
            (this as any)._containerElement = null;
            this._shadowRoot = null;
            this._innerRoot = null;
            this._watchedAppShadowRoot = null;
            this._mirroredStyles.clear();
            this._disconnectObserver();
            this._createContainer();
        } else {
            // Container is still live, but dms-app may have been replaced with a
            // new custom-element instance (new shadow root) since the observer
            // was wired up.  Re-adopt so future styles from the new root are
            // forwarded; the _mirroredStyles Set prevents re-adding what is
            // already in the overlay shadow root.
            const currentAppSR = this._document.querySelector('dms-app')?.shadowRoot ?? null;
            if (currentAppSR && currentAppSR !== this._watchedAppShadowRoot && this._shadowRoot) {
                this._disconnectStyleObserver();
                this._copyStylesToShadowRoot(currentAppSR, this._shadowRoot);
            }
            // Re-sync theme/scheme on every open: the clientTheme input may have
            // been set after the container was first created (ngOnChanges fires
            // before the first user action that opens an overlay, but after the
            // CDK may have lazily created the container).
            this._syncSchemeFromBody();
        }
        return this._containerElement;
    }

    // ── Container creation ──────────────────────────────────────────────────

    protected override _createContainer(): void {
        this._document
            .querySelectorAll('body > .dms-overlay-host')
            .forEach((el) => el.remove());

        this._document
            .querySelectorAll('style[data-dms-overrides-style]')
            .forEach((el) => el.remove());

        const host = this._document.createElement('div');
        host.classList.add('dms-overlay-host');
        host.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;z-index:99999;';
        this._document.body.appendChild(host);

        this._shadowRoot = host.attachShadow({ mode: 'open' });

        const innerRoot = this._document.createElement('div');
        innerRoot.classList.add('dms-overlay-inner-root');
        this._innerRoot = innerRoot;
        this._shadowRoot.appendChild(innerRoot);

        this._syncSchemeFromBody();

        const container = this._document.createElement('div');
        container.classList.add('cdk-overlay-container');
        innerRoot.appendChild(container);
        this._containerElement = container;

        this._adoptShadowStyles(this._shadowRoot);
        this._injectOverrideStyles(this._shadowRoot);
        this._observeBodyScheme();
    }

    // ── Scheme sync ─────────────────────────────────────────────────────────

    private _syncSchemeFromBody(): void {
        if (!this._innerRoot) return;
        const bodyClasses = this._document.body.classList;
        this._innerRoot.classList.toggle('dark', bodyClasses.contains('dark'));
        this._innerRoot.classList.toggle('light', !bodyClasses.contains('dark'));

        // clientTheme input takes precedence; fall back to host body's theme class.
        const themeClass = this._clientThemeClass ?? Array.from(bodyClasses).find(cls => cls.startsWith('theme-'));
        Array.from(this._innerRoot.classList)
            .filter(cls => cls.startsWith('theme-'))
            .forEach(cls => this._innerRoot!.classList.remove(cls));
        if (themeClass) this._innerRoot.classList.add(themeClass);
    }

    private _observeBodyScheme(): void {
        this._schemeObserver?.disconnect();
        this._schemeObserver = new MutationObserver(() => this._syncSchemeFromBody());
        this._schemeObserver.observe(this._document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });
    }

    // ── Override styles ─────────────────────────────────────────────────────

    private _injectOverrideStyles(shadowRoot: ShadowRoot): void {
        const style = this._document.createElement('style');
        style.setAttribute('data-dms-overrides-style', 'true');
        style.textContent = `
            .cdk-overlay-container {
                position: fixed;
                z-index: 1000;
                top: 0; left: 0;
                width: 100vw; height: 100vh;
                pointer-events: none;
            }
            .cdk-overlay-backdrop {
                position: absolute;
                top: 0; bottom: 0; left: 0; right: 0;
                z-index: 1000;
                pointer-events: auto;
                -webkit-tap-highlight-color: transparent;
                transition: opacity 400ms cubic-bezier(0.25, 0.8, 0.25, 1);
                opacity: 0;
            }
            .cdk-overlay-backdrop.cdk-overlay-backdrop-showing { opacity: 1; }
            .cdk-overlay-dark-backdrop { background: rgba(0,0,0,.32); }
            .cdk-overlay-pane {
                position: absolute;
                pointer-events: auto;
                box-sizing: border-box;
                z-index: 1000;
                display: flex;
                max-width: 100%; max-height: 100%;
            }
            .cdk-overlay-connected-position-bounding-box {
                position: absolute;
                z-index: 1000;
                display: flex;
                flex-direction: column;
                min-width: 1px; min-height: 1px;
            }
            .cdk-global-scrollblock { position: fixed; width: 100%; overflow-y: scroll; }
            .mat-mdc-dialog-container .mdc-dialog__surface {
                border-radius: 16px !important;
                padding: 24px !important;
            }
            .fuse-confirmation-dialog-panel .mat-mdc-dialog-container .mat-mdc-dialog-surface {
                padding: 0 !important;
            }
        `;
        shadowRoot.appendChild(style);
    }

    // ── Style adoption ──────────────────────────────────────────────────────

    private _adoptShadowStyles(targetShadowRoot: ShadowRoot): void {
        const appShadowRoot = this._document.querySelector('dms-app')?.shadowRoot;
        if (!appShadowRoot) {
            this._waitForShadowRoot(targetShadowRoot);
            return;
        }
        this._copyStylesToShadowRoot(appShadowRoot, targetShadowRoot);
    }

    private _copyStylesToShadowRoot(
        sourceShadowRoot: ShadowRoot,
        targetShadowRoot: ShadowRoot
    ): void {
        // Pre-copy filtered styles that are already in the source shadow root.
        sourceShadowRoot.querySelectorAll('style').forEach((styleEl) => {
            this._mirrorStyle(styleEl as HTMLStyleElement, targetShadowRoot, true);
        });

        // Forward every future style addition from the source shadow root.
        // No pattern filter here — custom dialog components that haven't rendered
        // yet won't match the overlay patterns, but their styles must still reach
        // the overlay when they do render.
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of Array.from(mutation.addedNodes)) {
                    if (
                        node instanceof HTMLStyleElement &&
                        !node.hasAttribute('data-dms-overlay-style')
                    ) {
                        this._mirrorStyle(node, targetShadowRoot, false);
                    }
                }
            }
        });

        observer.observe(sourceShadowRoot, { childList: true });
        (targetShadowRoot as any).__styleObserver = observer;
        this._watchedAppShadowRoot = sourceShadowRoot;
    }

    /**
     * Copies a single style element into the overlay shadow root, skipping it
     * if an identical stylesheet has already been mirrored (deduplication).
     *
     * @param applyPatternFilter  true for the initial bulk-copy (only
     *   overlay-relevant styles), false for observer-driven additions (all).
     */
    private _mirrorStyle(
        source: HTMLStyleElement,
        targetShadowRoot: ShadowRoot,
        applyPatternFilter: boolean
    ): void {
        const content = source.textContent ?? '';
        if (!content) return;
        if (applyPatternFilter && !this._isOverlayRelevantStyle(content)) return;

        const fingerprint = this._fingerprint(content);
        if (this._mirroredStyles.has(fingerprint)) return;

        const clone = source.cloneNode(true) as HTMLStyleElement;
        clone.setAttribute('data-dms-overlay-style', 'true');
        targetShadowRoot.appendChild(clone);
        this._mirroredStyles.add(fingerprint);
    }

    private _isOverlayRelevantStyle(content: string): boolean {
        return ShadowDomOverlayContainer._OVERLAY_PATTERNS.some(p => content.includes(p));
    }

    /** Cheap fingerprint: length + first 80 chars + last 80 chars. */
    private _fingerprint(content: string): string {
        return `${content.length}|${content.slice(0, 80)}|${content.slice(-80)}`;
    }

    // ── Wait for shadow root ────────────────────────────────────────────────

    private _waitForShadowRoot(targetShadowRoot: ShadowRoot): void {
        const watchTarget = this._document.querySelector('dms-app') ?? this._document.body;

        this._observer = new MutationObserver(() => {
            const appShadowRoot = this._document.querySelector('dms-app')?.shadowRoot;
            if (!appShadowRoot) return;
            this._disconnectObserver();
            this._copyStylesToShadowRoot(appShadowRoot, targetShadowRoot);
        });

        this._observer.observe(watchTarget, { childList: true, subtree: true });
    }

    // ── Cleanup ─────────────────────────────────────────────────────────────

    private _disconnectStyleObserver(): void {
        if (this._shadowRoot && (this._shadowRoot as any).__styleObserver) {
            (this._shadowRoot as any).__styleObserver.disconnect();
            delete (this._shadowRoot as any).__styleObserver;
        }
        this._watchedAppShadowRoot = null;
        // Do NOT clear _mirroredStyles here: the overlay shadow root itself has
        // not changed, so its contents are still valid.
    }

    private _disconnectObserver(): void {
        this._observer?.disconnect();
        this._observer = null;
        this._disconnectStyleObserver();
    }

    override ngOnDestroy(): void {
        this._schemeObserver?.disconnect();
        this._schemeObserver = null;
        this._disconnectObserver();
        this._containerElement?.getRootNode()?.parentElement?.remove();
        this._containerElement = null;
        this._shadowRoot = null;
        this._innerRoot = null;
        this._watchedAppShadowRoot = null;
        this._mirroredStyles.clear();
    }
}
